/*
 * Description:
 * ---------------------
 * Power & Cost — what this appliance costs to run.
 *
 * TWO AUDIENCES, ONE SCREEN
 *   The default view answers the question an executive actually asks: what does
 *   this cost, per site and across the fleet, per year. Four numbers and a
 *   sentence. The Detailed view answers the question an engineer asks when they
 *   do not believe the four numbers: which sensor, at which path, reading what,
 *   and which assumptions the arithmetic leaned on.
 *
 * THE PROVENANCE CHIP IS THE POINT
 *   Hardware differs. Some boxes measure their whole draw, some measure only
 *   the processor and have the rest added from an assumed baseline, and some
 *   measure nothing. Those must not look alike, because the number here ends up
 *   in a business case. The chip sits beside the headline figure, never in a
 *   footnote, and the Detailed view names every assumption with its value and
 *   whether it was configured or defaulted.
 *
 * ! file components/PowerPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useMemo, useState } from "react";
import {
  FiZap, FiDollarSign, FiCloud, FiInfo, FiAlertTriangle, FiActivity, FiClock,
} from "react-icons/fi";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useEdgeFeature, {
  FEATURE_ABSENT, FEATURE_PRESENT, FEATURE_UNREACHABLE,
} from "../hooks/useEdgeFeature";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import { fmtDuration } from "../utils/formatters";

const money = (v, cur) => {
  if (v == null || !isFinite(v)) return "—";
  const sym = { USD: "$", EUR: "€", GBP: "£" }[cur] || "";
  const s = v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(2);
  return sym ? `${sym}${s}` : `${s} ${cur || ""}`.trim();
};

const num = (v, d = 1) => (v == null || !isFinite(v) ? "—" : v.toFixed(d));

/* Sparkline over the watt series. Deliberately unlabelled and small: it is here
   to show steadiness, not to be read off. Gaps in the series are gaps in the
   line, because the sampler omits minutes it did not observe and drawing
   through them would assert draw that was never measured. */
function WattSpark({ points, height = 64 }) {
  const path = useMemo(() => {
    if (!points || points.length < 2) return null;
    const ys = points.map((p) => p.watts);
    const lo = Math.min(...ys);
    const hi = Math.max(...ys);
    const span = hi - lo || 1;
    const t0 = new Date(points[0].ts).getTime();
    const t1 = new Date(points[points.length - 1].ts).getTime();
    const tspan = t1 - t0 || 1;
    // Break the path wherever the gap exceeds two sample slots, so a restart
    // reads as a break rather than as a straight line through it.
    let d = "";
    let prevT = null;
    points.forEach((p) => {
      const t = new Date(p.ts).getTime();
      const x = ((t - t0) / tspan) * 100;
      const y = height - ((p.watts - lo) / span) * (height - 8) - 4;
      d += prevT !== null && t - prevT <= 150000 ? ` L${x.toFixed(2)},${y.toFixed(2)}` : ` M${x.toFixed(2)},${y.toFixed(2)}`;
      prevT = t;
    });
    return { d: d.trim(), lo, hi };
  }, [points, height]);

  if (!path) {
    return <div className="pwr-kpi-note">Not enough samples yet for a trend.</div>;
  }
  return (
    <div>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
        <path d={path.d} fill="none" stroke="#0891B2" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="pwr-kpi-note">
        {num(path.lo)} W low · {num(path.hi)} W high · {fmtDuration(points.length * 60)} with data
      </div>
    </div>
  );
}

// The sensors table's columns, also named beside each value where a phone draws a row as a card.
const SENSOR_COLS = { sensor: "Sensor", covers: "Covers", reading: "Reading", total: "In total", path: "Path" };

export default function PowerPanel({ selectedNode }) {
  const [view, setView] = useState("exec"); // "exec" | "detail"
  const [series, setSeries] = useState([]);

  // One probe answers both questions: does this build serve /power, and what
  // does it say. A separate "is it supported" request would double the traffic
  // to say nothing more.
  const { state, payload, refresh } = useEdgeFeature(selectedNode, "/power");

  useVisibleInterval(refresh, 15000, !!selectedNode);

  useVisibleInterval(
    async () => {
      if (!selectedNode || state !== FEATURE_PRESENT) return;
      const url = nodeApiUrlFor(selectedNode)("/power/series?minutes=180");
      const res = await fetchWithTimeout(url).catch(() => null);
      if (!res?.ok) return;
      const body = await res.json().catch(() => null);
      setSeries(body?.points || []);
    },
    30000,
    !!selectedNode && state === FEATURE_PRESENT
  );

  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a node to see what it costs to run.</div></div>;
  }

  if (state === FEATURE_ABSENT) {
    return (
      <FeatureUnavailableNotice
        nodeId={selectedNode}
        feature="Power & cost reporting"
        detail="This appliance is running a build from before power reporting shipped. Once it is
                updated, its draw, energy and cost appear here automatically. Nothing needs to be
                configured on the box, and none of its other panels are affected."
      />
    );
  }

  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel" style={{ borderLeft: "4px solid #f59e0b" }}>
        <div className="panel-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FiAlertTriangle size={17} color="#f59e0b" /> Could not read power from {selectedNode}
        </div>
        <div className="panel-sub">
          The request did not come back. This is a reachability problem, not a missing feature —
          the panel keeps retrying and will fill in on its own.
        </div>
      </div>
    );
  }

  if (state !== FEATURE_PRESENT || !payload) {
    return <div className="panel"><div className="panel-sub">Reading power…</div></div>;
  }

  const { available, status, reason, headline: h = {}, detail: d = {} } = payload;

  /* The route answered but there is no figure yet, and the three reasons for
     that are not interchangeable. Warming up clears itself within a sample or
     two and deserves reassurance; no sensor is permanent for this hardware and
     an operator told to "wait for an update" would waste an afternoon; switched
     off is a config line someone chose. Branching on `status` rather than on
     the prose keeps the edge's wording out of this file's logic. */
  if (!available) {
    if (status === "warming_up") {
      return (
        <div className="feat-na">
          <span className="feat-na-ico"><FiClock size={18} /></span>
          <div className="feat-na-body">
            <strong>Taking the first readings on {selectedNode}.</strong> An energy counter means
            nothing until it has been read twice, so the first figure lands a few seconds after the
            service starts.
            <div className="feat-na-hint">This panel fills in on its own — nothing to do.</div>
          </div>
        </div>
      );
    }
    if (status === "disabled") {
      return (
        <div className="feat-na">
          <span className="feat-na-ico"><FiInfo size={18} /></span>
          <div className="feat-na-body">
            <strong>Power reporting is switched off on {selectedNode}.</strong> {reason}
            <div className="feat-na-hint">
              Set <code>NABX_POWER_ENABLED=true</code> in the appliance&rsquo;s <code>edge.env</code>
              {" "}and restart the service to turn it back on.
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div className="feat-na">
          <span className="feat-na-ico"><FiInfo size={18} /></span>
          <div className="feat-na-body">
            <strong>{selectedNode} cannot measure its own power draw.</strong> {reason}
            <div className="feat-na-hint">
              This is a property of the hardware, not of the software — an update will not change
              it. A rating can be supplied instead with <code>NABX_POWER_NAMEPLATE_W</code>, which
              produces a clearly-labelled estimate rather than a measurement.
            </div>
          </div>
        </div>
        {d.sources?.length > 0 && <SourcesTable sources={d.sources} />}
      </div>
    );
  }

  const measured = h.confidence === "measured";
  /* The box measured its processor and nothing else, and nothing was invented
     to fill the gap. There is no appliance total, so there is no cost and no
     carbon either: pricing a package reading as though it were the box would be
     the same fabrication in a different unit. */
  const processorOnly = h.scope === "processor";
  /* A vendor envelope is published as a range and stays one here. Collapsing
     6.5-to-10 W to 8.25 W would invent a precision the manufacturer never
     claimed, and it is the width of the range that tells a reader how much a
     ten-minute measurement would be worth. */
  const isRange = h.watts_high > h.watts_low + 0.05;
  const vendor = h.provenance === "vendor_spec";
  const wattsText = isRange ? `${num(h.watts_low)}–${num(h.watts_high)}` : num(h.watts_avg24);
  const costText = isRange
    ? `${money(h.cost_year_low, h.currency)}–${money(h.cost_year_high, h.currency)}`
    : money(h.cost_year, h.currency);

  /* How much of the headline is actually a reading. On a CPU-only appliance an
     idling package beside a six-watt baseline makes this figure 90%+
     assumption, and a confident number with no composition beside it would be
     quoted as though it were measured. The bar is not decoration, it is the
     disclosure.

     The split is computed against the 24-hour average, because that is the
     number it sits under. The edge reports the instantaneous split, and quoting
     that beneath an averaged headline gives two figures that do not add up. The
     assumed part is a constant — the baseline does not vary with load — so
     subtracting it from the average is exact rather than a second estimate. */
  const assumedW = h.assumed_w || 0;
  const headlineW = h.watts_avg24 || 0;
  const measuredAvgW = Math.max(0, headlineW - assumedW);
  const assumedShare = headlineW > 0 && assumedW > 0 ? Math.min(1, assumedW / headlineW) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      {/* ── Headline ── */}
      <div className="pwr-hero">
        <div className="pwr-hero-main">
          <div className="pwr-hero-label">
            {processorOnly
              ? "Processor draw · 24-hour average"
              : vendor
              ? "Appliance draw · idle"
              : "Average draw · last 24 hours"}
          </div>
          <div className="pwr-hero-watts" style={isRange ? { fontSize: "2.6rem" } : undefined}>
            {wattsText}<span>W</span>
          </div>
          <div className="pwr-hero-sub">
            {processorOnly
              ? `CPU package only. Right now ${num(h.watts)} W.`
              : vendor
              ? `Processor measured at ${num(h.measured_w, 2)} W of it, the rest from the published figure.`
              : `${h.comparison ? `Roughly ${h.comparison}.` : ""} Right now ${num(h.watts)} W.`}
          </div>
          {/* A label, not a control: a dot and words, nothing shaped like a button. */}
          <div className={`pwr-chip ${measured ? "pwr-chip--measured" : "pwr-chip--estimated"}`}>
            {measured ? "Measured at the appliance" : vendor ? "From the manufacturer's specification" : "Estimated, not measured at the wall"}
          </div>
          {assumedShare > 0 && (
            <div className="pwr-split">
              <div
                className="pwr-split-bar"
                title={`${num(measuredAvgW, 2)} W measured, ${num(assumedW, 2)} W assumed`}
              >
                <span className="pwr-split-measured" style={{ width: `${(1 - assumedShare) * 100}%` }} />
              </div>
              <div className="pwr-split-text">
                <strong>{num(measuredAvgW, 2)} W</strong> read from the processor
                {" + "}
                <strong>{num(assumedW, 1)} W</strong> assumed for the rest of the box
                {assumedShare >= 0.6 && (
                  <> — most of this figure is the assumption, not a reading</>
                )}
              </div>
            </div>
          )}
        </div>
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          {processorOnly ? (
            <>
              <div className="pwr-hero-label">Cost to run, one site</div>
              <div className="pwr-hero-watts" style={{ fontSize: "1.5rem", lineHeight: 1.25 }}>
                Not yet known
              </div>
              <div className="pwr-hero-sub">
                This box has no sensor for the whole appliance, only for its processor. Rather than
                fill the gap with a guess and price that, it reports what it measured and stops.
              </div>
            </>
          ) : (
            <>
              <div className="pwr-hero-label">Cost to run, one site</div>
              <div className="pwr-hero-watts" style={{ fontSize: isRange ? "1.9rem" : "2.4rem" }}>
                {costText}<span>/year</span>
              </div>
              <div className="pwr-hero-sub">
                {isRange
                  ? "Electricity only, at idle. The width of this range is what one wall reading would remove."
                  : `${money(h.cost_month, h.currency)} a month · ${num(h.co2_kg_year, 0)} kg CO₂e a year`}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── View toggle ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div className="pwr-toggle">
          <button type="button" className={view === "exec" ? "on" : ""} onClick={() => setView("exec")}>Summary</button>
          <button type="button" className={view === "detail" ? "on" : ""} onClick={() => setView("detail")}>Detailed</button>
        </div>
        {!measured && (
          <div className="pwr-kpi-note" style={{ marginTop: 0 }}>
            Switch to Detailed to see exactly which part of this was measured and which was assumed.
          </div>
        )}
      </div>

      {processorOnly && <BaselineCallout headline={h} detail={d} />}
      {vendor && <VendorCallout headline={h} />}

      {view === "exec" ? (
        <>
          {processorOnly ? (
            <div className="pwr-kpis">
              <Kpi icon={<FiZap size={14} />} label="Processor energy, last 24 h"
                   value={`${num(h.kwh_24h, 3)} kWh`} note="The package alone, measured" />
              <Kpi icon={<FiActivity size={14} />} label="Processor draw now"
                   value={`${num(h.watts, 2)} W`} note="Live RAPL reading" />
            </div>
          ) : (
          <div className="pwr-kpis">
            <Kpi icon={<FiZap size={14} />} label="Energy, last 24 h" value={`${num(h.kwh_24h, 2)} kWh`}
                 note={h.month_is_projected ? "Month figure below is projected from this" : "Measured over the full day"} />
            <Kpi icon={<FiActivity size={14} />} label="Energy per month" value={`${num(h.kwh_month, 1)} kWh`}
                 note={h.month_is_projected ? "Projected — under 30 days of history" : "Actual, last 30 days"} />
            <Kpi icon={<FiDollarSign size={14} />} label="Cost per month" value={money(h.cost_month, h.currency)}
                 note={`At ${money(assumptionValue(d, "tariff"), h.currency)}/kWh`} />
            <Kpi icon={<FiCloud size={14} />} label="Carbon per month" value={`${num(h.co2_kg_month, 1)} kg`}
                 note={`At ${num(assumptionValue(d, "grid_carbon"), 0)} g/kWh`} />
          </div>
          )}

          {!processorOnly && h.fleet_sites > 1 && (
            <div className="panel">
              <div className="panel-title">Across the fleet</div>
              <div className="panel-sub">
                At {h.fleet_sites} sites drawing what this one draws, the fleet costs{" "}
                <strong>{money(h.fleet_cost_year, h.currency)} a year</strong> in electricity and emits{" "}
                <strong>{num(h.co2_kg_year * h.fleet_sites, 0)} kg CO₂e</strong>. Extrapolated from this
                appliance, not measured at the others.
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-title">Draw over the last three hours</div>
            <WattSpark points={series} />
          </div>
        </>
      ) : (
        <Detailed detail={d} headline={h} />
      )}
    </div>
  );
}

/* What one measurement would unlock. This is the whole remedy for a box that
   can only see its processor, so it is stated as an instruction with the box's
   own live number already substituted in, not as a documentation reference. */
function BaselineCallout({ headline, detail }) {
  const cpu = num(headline.watts_avg24, 2);
  return (
    <div className="feat-na">
      <span className="feat-na-ico"><FiInfo size={18} /></span>
      <div className="feat-na-body">
        <strong>One measurement turns this into an appliance figure.</strong> RAPL reads the
        processor package and nothing else, and this board exposes no sensor for the rest of
        itself, so the fan, radios, storage, PHYs, memory and power-supply loss are all invisible
        here. There is no way to derive them, only to measure them once.
        <div className="feat-na-hint">
          Read the box at the wall while it idles, with a plug meter, smart plug, PoE switch port
          or a UPS. Subtract the <strong>{cpu} W</strong> above from that reading and set the
          remainder as <code>NABX_POWER_BASELINE_W</code> in <code>edge.env</code>. Cost, carbon and
          the fleet projection appear immediately, and one reading covers every appliance of the
          same chassis. Connecting a UPS that reports draw skips the arithmetic entirely: the box
          would then measure itself and report as <em>measured</em>.
        </div>
      </div>
    </div>
  );
}

/* A manufacturer figure is not a measurement of this unit, and the difference
   is the whole reason this box exists rather than a hardcoded number. State the
   citation, state what it does not cover, state what would replace it. */
function VendorCallout({ headline }) {
  return (
    <div className="feat-na">
      <span className="feat-na-ico"><FiInfo size={18} /></span>
      <div className="feat-na-body">
        <strong>This is the manufacturer&rsquo;s figure for the chassis, not a reading from this
        unit.</strong>{" "}
        {headline.vendor_source}. This board carries no sensor on its power input, so nothing in
        software can measure the fan, radios, storage, PHYs, memory or supply loss. Only the
        processor can be read, and it measures {num(headline.measured_w, 2)} W of the figure above.
        <div className="feat-na-hint">
          The range is the manufacturer&rsquo;s own, published as a range and kept as one here.
          {headline.vendor_load_max_w > 0 && (
            <> Under sustained load with radios and modems active the same specification quotes up
            to {num(headline.vendor_load_max_w, 0)} W, so treat the figure above as the idle case
            rather than a ceiling.</>
          )}{" "}
          A single wall reading with a plug meter, smart plug or UPS replaces all of it with a
          measurement of this actual box.
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, note }) {
  return (
    <div className="pwr-kpi">
      <div className="pwr-kpi-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>{icon}{label}</div>
      <div className="pwr-kpi-value">{value}</div>
      {note && <div className="pwr-kpi-note">{note}</div>}
    </div>
  );
}

function assumptionValue(detail, key) {
  const a = (detail?.assumptions || []).find((x) => x.key === key);
  return a ? a.value : null;
}

function SourcesTable({ sources }) {
  return (
    <div className="panel">
      <div className="panel-title">What was read</div>
      <div className="panel-sub">
        Every path here can be checked on the box with one <code>cat</code>. Sub-domains marked
        excluded are inside another reading already and would double the total if summed.
      </div>
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table className="pwr-table cards-on-phone">
          <thead>
            <tr>{Object.values(SENSOR_COLS).map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.path}>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td data-label={SENSOR_COLS.covers}>{s.kind === "platform" ? "whole appliance" : "processor package"}</td>
                <td data-label={SENSOR_COLS.reading}>{s.ok ? `${num(s.watts, 2)} W` : <span style={{ color: "#94a3b8" }}>no reading</span>}</td>
                <td data-label={SENSOR_COLS.total}>
                  {s.in_headline
                    ? <span className="pwr-tag">counted</span>
                    : <span className="pwr-tag pwr-tag--excluded">excluded</span>}
                </td>
                <td data-label={SENSOR_COLS.path}><code>{s.path}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Detailed({ detail, headline }) {
  const used = (detail.assumptions || []).filter((a) => a.used);
  const unused = (detail.assumptions || []).filter((a) => !a.used);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <SourcesTable sources={detail.sources || []} />

      {headline.assumed_w > 0 && (
        <div className="panel">
          <div className="panel-title">What goes into this figure</div>
          <div className="panel-sub">
            Of the {num(headline.watts_avg24, 2)} W daily average,{" "}
            <strong>{num(Math.max(0, (headline.watts_avg24 || 0) - (headline.assumed_w || 0)), 2)} W</strong>{" "}
            came from a sensor and <strong>{num(headline.assumed_w, 1)} W</strong> came from the
            baseline assumption below. The assumed part is a constant, so it does not move with
            load: at idle it is nearly the whole figure. To replace the assumption
            with a measurement, put a plug meter on one appliance of this model, subtract the
            processor reading above from the wall figure, and set the remainder as{" "}
            <code>NABX_POWER_BASELINE_W</code>. Every site with the same chassis then reports a
            number worth quoting.
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-title">What was assumed</div>
        <div className="panel-sub">
          {used.length === 0
            ? "Nothing. Every figure above came from a sensor."
            : "These coefficients are site properties, not measurements. Each is listed with its " +
              "value and whether it was configured for this site or left at the default."}
        </div>
        {used.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table className="pwr-table">
              <thead><tr><th>Assumption</th><th>Value</th><th>Set by</th></tr></thead>
              <tbody>
                {used.map((a) => (
                  <tr key={a.key}>
                    <td>{a.label}</td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {a.value} {a.unit}
                    </td>
                    <td>
                      <span className={`pwr-tag ${a.source === "configured" ? "pwr-tag--configured" : ""}`}>
                        {a.source === "configured" ? "configured for this site" : "default"}
                      </span>
                      {a.citation && (
                        <div className="pwr-kpi-note" style={{ marginTop: 4 }}>{a.citation}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {unused.length > 0 && (
          <div className="pwr-kpi-note" style={{ marginTop: 10 }}>
            Not used by this reading: {unused.map((a) => a.label.toLowerCase()).join("; ")}.
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-title">Sampling</div>
        <div className="pwr-kpis" style={{ marginTop: 12 }}>
          <Kpi label="Sample interval" value={`${num(detail.sample_interval_sec, 0)} s`} />
          <Kpi label="Samples taken" value={(detail.samples ?? 0).toLocaleString()} />
          <Kpi label="Intervals discarded" value={(detail.gaps ?? 0).toLocaleString()}
               note="Spans across a restart or a suspend, dropped rather than billed" />
          <Kpi label="Counter wraps" value={(detail.wraps ?? 0).toLocaleString()}
               note="RAPL counters roll over; each was corrected" />
          <Kpi label="Energy since first boot" value={`${num(detail.lifetime_kwh, 3)} kWh`} />
          <Kpi label="Month covered"
               value={`${(detail.coverage_hours || 0) < 24
                 ? fmtDuration((detail.coverage_hours || 0) * 3600)
                 : Math.min(30, Math.round(detail.coverage_hours / 24))} of 30 days`}
               note={headline.month_is_projected ? "Month figure is projected" : "Month figure is actual"} />
        </div>
      </div>
    </div>
  );
}
